/**
 * Settings → the three optional sign-up consents, to give or withdraw at any
 * time (Article 7(3) GDPR: withdrawing must be as easy as giving).
 *
 * Saving sends all three, so an unticked box is a withdrawal. The backend
 * then refuses new health writes or AI calls; what is already stored stays
 * until the traveller deletes it, which the card says.
 */

import ConsentChoices from "@/components/consent/ConsentChoices";
import { useI18nStore } from "@/stores/i18nStore";
import {
  CORE_CONSENTS_QUERY,
  type CoreDecisions,
  getCoreConsents,
  isGranted,
  saveCoreConsents,
} from "@/utils/coreConsents";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

export const ConsentSettingsCard: React.FC = () => {
  const { t } = useI18nStore();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: CORE_CONSENTS_QUERY,
    queryFn: getCoreConsents,
  });
  const [choices, setChoices] = useState<CoreDecisions>({});

  useEffect(() => {
    if (!data) return;
    setChoices({
      HEALTH_DATA: isGranted(data, "HEALTH_DATA"),
      AI_PROCESSING: isGranted(data, "AI_PROCESSING"),
      MARKETING_COMMUNICATIONS: isGranted(data, "MARKETING_COMMUNICATIONS"),
    });
  }, [data]);

  const save = useMutation({
    mutationFn: saveCoreConsents,
    onSuccess: (state) => {
      queryClient.setQueryData(CORE_CONSENTS_QUERY, state);
      toast.success(t.consentSaved);
    },
    onError: () => toast.error(t.consentSaveFailed),
  });

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
        <ShieldCheck className="w-5 h-5 text-primary-600" />
        <span>{t.settingsConsentsTitle}</span>
      </h2>
      <p className="mb-5 text-sm text-gray-600">{t.settingsConsentsLead}</p>
      <ConsentChoices
        value={choices}
        onChange={setChoices}
        showTerms={false}
        disabled={!data || save.isPending}
      />
      <button
        type="button"
        disabled={!data || save.isPending}
        onClick={() =>
          save.mutate({
            HEALTH_DATA: choices.HEALTH_DATA === true,
            AI_PROCESSING: choices.AI_PROCESSING === true,
            MARKETING_COMMUNICATIONS: choices.MARKETING_COMMUNICATIONS === true,
          })
        }
        className="mt-5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
      >
        {t.consentGateSave}
      </button>
    </div>
  );
};

export default ConsentSettingsCard;
